/**
 * Decisions Browser — cross-playbook AGENT_DECISION query view.
 *
 * Filters: decision_type, agent_type, client (from selected client),
 * time window. Each row shows the basics; clicking expands a typed
 * inspector. Persona-routing decisions use the structured
 * PersonaRoutingDecisionCard; everything else falls back to a generic
 * key/value dump from metadata.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  History,
  RefreshCw,
  AlertCircle,
  Filter,
  Cpu,
  Bot,
  ShieldCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useClientStore } from '@/stores/client';
import {
  creativeMemoryApi,
  type CreativeMemoryActivityItem,
} from '@/services/api';
import { PersonaRoutingDecisionCard } from './components/PersonaRoutingDecisionCard';

const sinceFromDays = (n: number | null): string | undefined => {
  if (n === null) return undefined;
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

const DECISION_TYPES = [
  '(any)',
  'persona_routing',
  'rule_validation',
  'mcp_tool',
] as const;

const AGENT_TYPES = [
  '(any)',
  'persona_concept_graph_ranker',
  'creative_review_agent',
  'creative_agent',
  'human',
] as const;

export function DecisionsBrowserPage() {
  const selectedClient = useClientStore((s) => s.selectedClient());
  const clientUid = selectedClient?.client_uid;

  const [decisionType, setDecisionType] = useState<string>('(any)');
  const [agentType, setAgentType] = useState<string>('(any)');
  const [windowDays, setWindowDays] = useState<number | null>(7);
  const [items, setItems] = useState<CreativeMemoryActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    creativeMemoryApi
      .listDecisions({
        decision_type:
          decisionType === '(any)' ? undefined : decisionType,
        agent_type: agentType === '(any)' ? undefined : agentType,
        client_uid: clientUid,
        since: sinceFromDays(windowDays),
        limit: 100,
      })
      .then((resp) => {
        setItems(resp.items);
        setTotal(resp.total);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientUid, decisionType, agentType, windowDays]);

  return (
    <div className="px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-sky-500" />
            <h1 className="text-xl font-semibold text-slate-800">Decisions</h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Every AGENT_DECISION episode written by a Creative Memory–aware
            agent, scoped to the selected client and queryable across all
            playbooks. v1 is a JSONL scanner; the upcoming indexed
            implementation (PRD §4) preserves the same wire shape.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-slate-300 disabled:opacity-50"
        >
          <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
          Refresh
        </button>
      </header>

      {/* Filter strip */}
      <div className="mb-5 flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-slate-500">
          <Filter className="w-3 h-3" />
          Filter
        </span>
        <Select
          label="decision_type"
          value={decisionType}
          onChange={setDecisionType}
          options={DECISION_TYPES as unknown as string[]}
        />
        <Select
          label="agent_type"
          value={agentType}
          onChange={setAgentType}
          options={AGENT_TYPES as unknown as string[]}
        />
        <WindowSelect value={windowDays} onChange={setWindowDays} />
        <span className="ml-auto text-[10px] text-slate-500">
          {items.length} of {total} shown · client{' '}
          <span className="font-mono">
            {clientUid || '(all)'}
          </span>
        </span>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
          <div>
            <p className="font-semibold">Failed to load decisions</p>
            <p className="mt-0.5 text-rose-700">{error}</p>
          </div>
        </div>
      )}

      {!error && !loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center text-xs text-slate-500">
          No decisions match the current filters.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map((d) => (
            <DecisionRow key={d.episode_id} decision={d} />
          ))}
        </div>
      )}
    </div>
  );
}

function DecisionRow({ decision }: { decision: CreativeMemoryActivityItem }) {
  if (decision.decision_type === 'persona_routing') {
    return <PersonaRoutingDecisionCard decision={decision} />;
  }
  return <GenericDecisionCard decision={decision} />;
}

function GenericDecisionCard({
  decision,
}: {
  decision: CreativeMemoryActivityItem;
}) {
  const Icon =
    decision.episode_type === 'rule_validation'
      ? ShieldCheck
      : decision.agent_type === 'human'
        ? Bot
        : Bot;
  return (
    <article className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[11px] font-semibold text-slate-700">
          {decision.decision_type || decision.action || decision.episode_type}
        </span>
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-mono text-slate-600">
          {decision.agent_type || 'system'}
        </span>
        {decision.playbook_id && (
          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-mono text-amber-700">
            pb {decision.playbook_id}
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">
          {new Date(decision.ts).toLocaleString()}
        </span>
      </header>
      {(decision.decision || decision.rationale) && (
        <p className="mt-1.5 text-[12px] text-slate-700 leading-snug">
          {decision.decision || decision.rationale}
        </p>
      )}
      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-slate-500">
        {decision.ranker_model && (
          <span className="inline-flex items-center gap-1">
            <Cpu className="w-3 h-3" />
            {decision.ranker_model}
          </span>
        )}
        {decision.creative_id && (
          <span className="rounded bg-sky-50 px-1.5 py-0.5">
            creative {decision.creative_id}
          </span>
        )}
        {decision.persona_id && (
          <span className="rounded bg-violet-50 px-1.5 py-0.5">
            persona {decision.persona_id}
          </span>
        )}
        {decision.rule_id && (
          <span className="rounded bg-slate-100 px-1 py-0.5 font-mono">
            rule {decision.rule_id}
          </span>
        )}
      </div>
    </article>
  );
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}

function Select({ label, value, onChange, options }: SelectProps) {
  return (
    <label className="inline-flex items-center gap-1 text-xs text-slate-600">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </label>
  );
}

interface WindowSelectProps {
  value: number | null;
  onChange: (v: number | null) => void;
}

function WindowSelect({ value, onChange }: WindowSelectProps) {
  return (
    <label className="inline-flex items-center gap-1 text-xs text-slate-600">
      <span className="text-[10px] uppercase tracking-wide text-slate-500">
        window
      </span>
      <select
        value={value === null ? 'all' : String(value)}
        onChange={(e) =>
          onChange(e.target.value === 'all' ? null : Number(e.target.value))
        }
        className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal/40"
      >
        <option value="1">24h</option>
        <option value="7">7d</option>
        <option value="30">30d</option>
        <option value="all">all time</option>
      </select>
    </label>
  );
}
